import {defineStore} from 'pinia'
import {useAuthStore} from "@/stores/auth"
import {emitter} from "@/emitter"
import {computed, h, ref} from "vue"
import {NButton} from "naive-ui"
import {type NotificationObject, useGlobalActions} from "@/composables/useGlobalActions"
import _uniqBy from "lodash/uniqBy"
import {useStoreI18n} from "@/composables/useStoreI18n"
import dayjs from "@/utils/dayjs"
import {http} from "@/utils/http"
import {Logger} from "@/utils"
import {Capacitor} from '@capacitor/core'
import {App} from '@capacitor/app'


export type NotificationCategory = "message" | "reminder" | "alert" | "news" | string
export type NotificationType = "success" | "info" | "warning" | "error" | "default" | undefined

export interface Notification {
    action?: () => void
    actionTitle?: string
    category: NotificationCategory
    date: string
    description: string
    id: number | string
    read: boolean
    timestamp: number
    title: string
    type: NotificationType
}

export interface PrependOptions {
    /** prepend and send a notification */
    sendNotify?: boolean
    /** send a notification only if there isn't any item with match id/type/category */
    autoNotify?: boolean
}


export const useNotificationsStore = defineStore('notifications', () => {
    const authStore = useAuthStore()
    const globalActions = useGlobalActions()
    const i18n = useStoreI18n()

    const eventSource = ref<EventSource | null>(null)
    const list = ref<Notification[]>([])
    const lastFetchedTimestamp = ref<number>(0)


    function urlBase64ToUint8Array(base64String: string) {
        const padding = '='.repeat((4 - base64String.length % 4) % 4)
        const base64 = (base64String + padding)
            .replace(/-/g, '+')
            .replace(/_/g, '/')
        const rawData = window.atob(base64);
        const outputArray = new Uint8Array(rawData.length)
        for (let i = 0; i < rawData.length; ++i) {
            outputArray[i] = rawData.charCodeAt(i)
        }
        return outputArray;
    }

    function missingWebPushSubscription(): Promise<{ missing: boolean, registration?: ServiceWorkerRegistration }> {
        return new Promise((resolve) => {
            const VITE_WEBPUSH_SERVER_KEY = import.meta.env.VITE_WEBPUSH_SERVER_KEY
            const API_URL = import.meta.env.VITE_API_URL;

            if (navigator && navigator.serviceWorker) {
                navigator.serviceWorker.ready
                    .then((registration) => {
                        registration.pushManager.getSubscription()
                            .then((existingSubscription) => {
                                if (existingSubscription) {
                                    http.post(API_URL + '/webpush/', {
                                        body: {
                                            subscription: existingSubscription
                                        }
                                    }).then(() => {
                                        authStore.webPushEndpoint = existingSubscription.endpoint
                                        resolve({missing: false, registration})
                                    }).catch((error) => {
                                        throw new Error('Subscription konnte nicht gesendet werden.')
                                    })
                                } else {
                                    resolve({missing: true, registration})
                                }
                            })
                            .catch((error) => {
                                throw new Error('Subscription check failed.')
                            })
                    })
                    .catch((error) => {
                        Logger.debug('Service Worker not ready.')
                        resolve({missing: true})
                    })
            } else {
                Logger.info('Service Worker not supported.')
                resolve({missing: true})
            }
        })
    }

    function registerWebPushSubscription(registration: ServiceWorkerRegistration) {
        const VITE_WEBPUSH_SERVER_KEY = import.meta.env.VITE_WEBPUSH_SERVER_KEY
        const API_URL = import.meta.env.VITE_API_URL;

        // Create new subscription only if none exists
        return registration.pushManager
            .subscribe({
                userVisibleOnly: true,
                applicationServerKey: urlBase64ToUint8Array(VITE_WEBPUSH_SERVER_KEY),
            })
            .then((subscription) => {
                Logger.debug('Web-Push-Subscription im browser erfolgreich:', subscription)

                http.post(API_URL + '/webpush/', {
                    body: {
                        subscription: subscription
                    }
                }).then(() => {
                    authStore.webPushEndpoint = subscription.endpoint
                    Logger.debug('Web-Push-Subscription erfolgreich an den Server gesendet.')
                }).catch((error) => {
                    throw new Error('Subscription konnte nicht gesendet werden.')
                })
            })
            .catch((error) => {
                Logger.error('Web-Push-Subscription fehlgeschlagen:', error)
            })
    }

    function formatDate(date: Date) {
        return dayjs(date).format("YYYY-MM-DD") === dayjs().format("YYYY-MM-DD")
            ? dayjs(date).format("HH:mm")
            : dayjs(date).format("D MMM")
    }

    return {
        // state
        list,
        lastFetchedTimestamp,

        // getters
        hasUnread: computed(() => list.value.filter(o => !o.read).length !== 0),
        hasNotifications: computed(() => list.value.length !== 0),
        missingWebPushSubscription: missingWebPushSubscription,

        // actions
        // Manage Web Push Subscription
        initializeWebPushSubscription() {
            if (Capacitor.isNativePlatform()) {
                Logger.info('Web-Push-Subscription nicht möglich, da Native App.')
            } else {
                missingWebPushSubscription().then((result) => {
                    if (result.missing && result.registration) {
                        if (navigator.userAgent.includes('Safari') && !navigator.userAgent.includes('Chrome')) {
                            window.Notification
                                .requestPermission()
                                .then((permission) => {
                                    if (permission === 'granted') {
                                        Logger.debug('Web-Push-Notification permission granted');
                                        registerWebPushSubscription(result.registration)
                                    } else {
                                        Logger.debug('Web-Push-Notification permission denied')
                                    }
                                })
                        } else {
                            registerWebPushSubscription(result.registration)
                        }
                    }
                })
            }
        },
        unsubscribeWebPushSubscription(): Promise<void> {
            return new Promise((resolve, reject) => {
                if (!Capacitor.isNativePlatform() && navigator && navigator.serviceWorker) {
                    Logger.debug('Beende Web-Push-Subscription...')
                    navigator.serviceWorker.getRegistrations()
                        .then(async function (registrations) {
                            console.debug('Registrations', registrations)

                            if (registrations.length === 0) {
                                Logger.debug('Keine Web-Push-Subscriptions zum Beenden gefunden.')
                                resolve()
                                return
                            }

                            const API_URL = import.meta.env.VITE_API_URL

                            const unregisterPromises = registrations.map(function (registration) {
                                return registration.unregister()
                            })

                            await Promise.all(unregisterPromises)

                            if (authStore.webPushEndpoint && authStore.webPushEndpoint.length > 0) {
                                http.delete(API_URL + '/webpush/', {
                                    body: {
                                        subscription: {
                                            endpoint: authStore.webPushEndpoint
                                        }
                                    }
                                }).then(() => {
                                    authStore.webPushEndpoint = ''
                                    Logger.debug('Web-Push-Subscription unsubscripe am Server erfolgreich.')
                                }).catch((error) => {
                                    throw new Error('Web-Push-Subscription unsubscripe am Server fehlgeschlagen.')
                                })
                            }

                            Logger.debug(`${registrations.length} Web-Push-Subscription(s) erfolgreich beendet.`)
                            resolve()
                        })
                        .catch(function (error) {
                            Logger.error('Web-Push-Subscription beenden fehlgeschlagen:', error)
                            resolve()
                        })
                } else {
                    Logger.debug('Web-Push-Subscription beenden übersprungen (Native Platform oder Service Worker nicht verfügbar)')
                    resolve()
                }
            })
        },
        // Manage Mercure Subscription
        initializeMercureSubscription() {
            if (!authStore.logged) {
                Logger.info('Mercure-Subscription nicht initialisiert, da der Nutzer nicht eingeloggt ist.')
                return
            }
            if (eventSource.value !== null) {
                Logger.info('Mercure-Subscription bereits initialisiert.')
                return
            }

            const topicUrlPublic = '/members/public'
            const topicUrlPrivate = '/member/' + authStore.user.id
            const MERCURE_URL = import.meta.env.VITE_MERCURE_URL
            const url = `${MERCURE_URL}?topic=${encodeURIComponent(topicUrlPublic)}&topic=${encodeURIComponent(topicUrlPrivate)}&authorization=${authStore.user.mercure}`

            Logger.info('Initialisiere Mercure-Subscription...', url)

            eventSource.value = new EventSource(url)

            // Handle incoming messages
            eventSource.value.onmessage = (event) => {
                if (!event.data || typeof event.data !== 'string' || event.data.length === 0) return
                const data = JSON.parse(event.data)
                Logger.debug('Remote Event (Mercure):', data)
                const type = data.data?.type
                emitter.emit(`remoteevent:${type}`, data)
            }

            eventSource.value.onerror = (error) => {
                Logger.error('Error in Mercure EventSource:', error)
            }
        },
        closeMercureSubscription(): Promise<void> {
            return new Promise((resolve, reject) => {
                if (eventSource.value !== null) {
                    if (typeof eventSource.value.close === 'function') {
                        Logger.debug('Beende Mercure-Subscription...')
                        eventSource.value.close()
                    }
                    eventSource.value = null
                }
                resolve()
            })
        },
        // Manage App Push Notifications
        async updateAppPushNotificationSignIn() {
            const API_URL = import.meta.env.VITE_API_URL;

            // The token is required for all app push notifications
            // If the user logged in, then will the notifications with this token assigned to the user,
            // on else the notifications assigned to the app
            const _call = (appVersion = '') => {
                http.post(API_URL + '/appevent/signin', {
                    body: {
                        pushToken: authStore.pushToken ?? '',
                        webPushEndpoint: authStore.webPushEndpoint ?? '',
                        platform: Capacitor.getPlatform(),
                        appVersion: appVersion,
                        compileVersion: __BUILD_DATE__
                    }
                })
                    .then((result) => {
                        Logger.debug('App Push Notification token updated', result)
                    })
                    .catch(() => {
                    })
            }

            if (Capacitor.isNativePlatform()) {
                App.getInfo().then((info) => {
                    console.debug('App Info', info)
                    _call(info.version)
                })
            } else {
                _call()
            }
        },
        updateAppPushNotificationSignOut(): Promise<void> {
            const API_URL = import.meta.env.VITE_API_URL;

            // The token is required for all app push notifications
            // If the user logged in, then will the notifications with this token assigned to the user,
            // on else the notifications assigned to the app
            return new Promise<void>((resolve, reject) => {
                const _call = (appVersion = '') => {
                    Logger.debug('Beende App Push Notification...')
                    http.post(API_URL + '/appevent/signout', {
                        timeout: 2000,
                        body: {
                            pushToken: authStore.pushToken ?? '',
                            webPushEndpoint: authStore.webPushEndpoint ?? '',
                            platform: Capacitor.getPlatform(),
                            appVersion: appVersion,
                            compileVersion: __BUILD_DATE__
                        }
                    })
                        .then((result) => {
                            Logger.debug('App Push Notification token updated', result)
                            resolve()
                        })
                        .catch(() => {
                            resolve()
                        })
                }

                if (Capacitor.isNativePlatform()) {
                    App.getInfo()
                        .then((info) => {
                            _call(info.version)
                        })
                        .catch((error) => {
                            Logger.debug('App.getInfo failed, using empty version:', error)
                            _call()
                        })
                } else {
                    _call()
                }
            })
        },
        // Manage Notifications
        fetchNotifications(maxItems: number = 7) {
            if (!authStore.logged) {
                return;
            }

            const API_URL = import.meta.env.VITE_API_URL;
            const locale = i18n.getLocale()


            http.get(API_URL + '/user/notifications/' + maxItems + '/' + lastFetchedTimestamp.value, {
                headers: {
                    'Accept-Language': locale
                },
            })
                .then((json) => {
                    let lastFetched = lastFetchedTimestamp.value

                    for (const key in json.data) {
                        const item = json.data[key]
                        if (item.sent > lastFetched)
                            lastFetched = item.sent

                        let title = item?.title || (item.data.type === 'system' ? 'System' : 'Notification')
                        const subtitle = item?.data?.title
                        if (subtitle && title !== subtitle) title = `${title}: ${subtitle}`

                        const notification: Notification = {
                            action: null,
                            actionTitle: null,
                            timestamp: item.sent,
                            date: formatDate(item.sent),
                            title: title,
                            description: item.body,
                            read: false, // message, reminder, alert, news
                            id: item.data.type + '.' + item.data.entity.id,
                            category: item.data.type === 'system' ? 'alert' : 'news',
                            type: item.data.type === 'system' ? 'warning' : 'info'
                        }

                        list.value.push(notification)
                        lastFetchedTimestamp.value = lastFetched
                    }
                })
                .catch((error) => {
                    Logger.error('Get notifications error:', error)
                })
        },
        setRead(id: string | number) {
            const item = list.value.find(o => o.id === id)
            if (item) {
                item.read = true
            }
        },
        setAllRead() {
            for (const item of list.value) {
                item.read = true
            }
        },
        deleteOne(id: string | number) {
            list.value = list.value.filter(o => o.id !== id)
        },
        deleteAll() {
            list.value = []
        },
        inject({
                   id = Date.now(),
                   title,
                   description,
                   type = 'info',
                   category = 'message',
                   read = false,
                   action,
                   actionTitle,
                   sendNotify = true,
                   autoNotify = false
               }: {
            id?: number | string
            title: string
            description: string
            type?: NotificationType
            category?: NotificationCategory
            read?: boolean
            action?: () => void
            actionTitle?: string
            sendNotify?: boolean
            autoNotify?: boolean
        }) {
            const timestamp = Date.now()
            const notification: Notification = {
                id,
                title,
                description,
                type,
                category,
                read,
                action,
                actionTitle,
                date: formatDate(new Date(timestamp)),
                timestamp
            }

            this.prepend(notification, {sendNotify, autoNotify})
        },
        prepend(newItem: Notification, options?: PrependOptions) {
            let sendNotify = options?.sendNotify || false
            const autoNotify = options?.autoNotify || false

            if (autoNotify) {
                const item = list.value.find(
                    o => o.id === newItem.id && o.type === newItem.type && o.category === newItem.category
                )

                if (!item) {
                    sendNotify = true
                }
            }

            if (sendNotify) {
                const notify: NotificationObject = {
                    title: newItem.title,
                    content: newItem.description,
                    type: newItem.type,
                    meta: newItem.date,
                    action: undefined,
                    duration: 3000,
                    keepAliveOnHover: true
                }

                if (newItem.action) {
                    notify.action = () =>
                        h(
                            NButton,
                            {
                                text: true,
                                type: newItem.type,
                                onClick: newItem.action
                            },
                            {
                                default: () => newItem.actionTitle || "Details"
                            }
                        )
                }

                globalActions.notification(notify)
            }

            list.value = _uniqBy([newItem, ...list.value], o => o.id)
        }
    }
}, {
    persist: true
})
